import "./App.scss";
import { CircularProgress } from "@mui/material";
import { newAccount } from "./components/Login";
import { Suspense, useEffect, useState } from "react";
import * as borogove from "borogove";
import { borogove_persistence_Sqlite as borogoveSqlite } from "borogove/sqlite-wasm";
import * as Sentry from "@sentry/browser";
import { stem } from "porter2";
import {
	Await,
	useAsyncValue,
	Outlet,
	useNavigate,
	useParams,
} from "react-router";
import { ios, webkit, href, useEffectEvent, showNotification } from "./util";
import { SettingsContext, SettingsData } from "./components/Settings";
import { Call } from "./components/Call";

Sentry.init({
	dsn: "https://ec9ae5aef68e4035937d4c5943c4156c@app.glitchtip.com/7125",

	integrations: [
		Sentry.browserTracingIntegration(),
		Sentry.replayIntegration(),
	],

	tracesSampleRate: 0.0,
	tracePropagationTargets: [],

	replaysSessionSampleRate: 0.0,
	replaysOnErrorSampleRate: 1.0,
});

const mediaStore = await borogove.persistence.MediaStoreCache("snikket");
const idbp = await borogove.persistence.IDB("snikket", mediaStore, null, stem);
let persistence = null;
if (typeof navigator.storage?.getDirectory === "function" && (webkit || ios)) {
	persistence = new borogoveSqlite("snikket.sqlite3", mediaStore);
	mediaStore.setKV(idbp); // Use IDB for media KV for now because we can't use opfs-sahpool from service worker
} else {
	persistence = idbp;
}

export type Accounts = Record<string, ReturnType<typeof newAccount>>;

export type Context = {
	accounts: Accounts;
	setAccounts: (
		accounts: Accounts | ((accounts: Accounts) => Accounts),
	) => void;
	persistence: borogove.Persistence & borogove.persistence.KeyValueStore;
	idbp: Awaited<ReturnType<typeof borogove.persistence.IDB>>;
	initialSelectedAccount: string | null;
	setSettings: (settings: SettingsData) => void;
};

const AppInner = () => {
	const { initialAccounts, initialSelectedAccount, initialSettings } =
		useAsyncValue() as {
			initialAccounts: Accounts;
			initialSelectedAccount: string;
			initialSettings: SettingsData;
		};
	const [accounts, setAccounts] = useState(initialAccounts);
	const [settings, setSettings] = useState<SettingsData>(initialSettings);
	const params = useParams();
	const navigate = useNavigate();

	const notificationEvent = useEffectEvent(
		(
			client: borogove.Client,
			message: borogove.ChatMessage,
			event: borogove.ChatMessageEvent,
		) => {
			(async () => {
				if (event !== borogove.ChatMessageEvent.DeliveryEvent) return;
				if (!message.isIncoming()) return;
				if (message.type === borogove.MessageType.MessageCall) return;
				const chat = client.getChat(message.chatId());
				if (!chat) return;

				if (
					!document.hidden &&
					document.hasFocus() &&
					message.chatId() === params.chatId
				)
					return;
				if (message.versions.length > 0) return; // No notify for edits

				if (
					!chat.notificationsFiltered() ||
					(chat.notifyMention() &&
						message.body().toPlainText().indexOf(client.displayName()) >= 0) ||
					(chat.notifyReply() &&
						message.replyToMessage &&
						chat.getParticipantDetails(message.replyToMessage.senderId)?.isSelf)
				) {
					if (settings.audibleNotifications) new Audio("/message.opus").play();
					await showNotification(client.accountId(), chat, message);
				}
			})();
		},
	);

	useEffect(() => {
		window.accounts = Object.freeze(accounts);

		const tokens = Object.fromEntries(
			Object.keys(accounts).map((accountId) => {
				const client = accounts[accountId].client;
				const setStatus = (status: string) =>
					setAccounts({
						...accounts,
						[accountId]: { ...accounts[accountId], status },
					});
				return [
					accountId,
					[
						client.addStatusOnlineListener(() => setStatus("online")),
						client.addStatusOfflineListener(() => setStatus("connecting")),
						client.addConnectionFailedListener(() => setStatus("error")),
						client.addPasswordNeededListener(() => setStatus("auth")),
						client.addChatMessageListener((message, event) =>
							notificationEvent(client, message, event),
						),
					],
				];
			}),
		);

		return () => {
			for (const [accountId, ts] of Object.entries(tokens)) {
				ts.map((t) => accounts[accountId].client.removeEventListener(t));
			}
		};
	}, [accounts]);

	useEffect(() => {
		if (!navigator.serviceWorker) return;
		const handler = (event: MessageEvent) => {
			const accountId = event.data?.data?.accountId;
			const chatId = event.data?.data?.chatId;
			if (event.data.event === "notificationclick") {
				navigate(
					href("/accounts/:accountId/chats/:chatId", { accountId, chatId }) +
						`#message:${event.data.data.messageId}`,
				);
				if (event.data.data.type === borogove.MessageType.MessageCall) {
					if (event.data.data.callStatus === "propose") {
						setTimeout(
							() => accounts[accountId]?.client?.getChat(chatId)?.acceptCall(),
							100,
						);
					}
				}
			}
			if (event.data.event === "notificationclose") {
				if (event.data.data.type === borogove.MessageType.MessageCall) {
					setTimeout(() => {
						const chat = accounts[accountId]?.client?.getChat(chatId);
						if (
							chat &&
							chat.callStatus() === borogove.calls.CallStatus.Incoming
						) {
							chat.hangup();
						}
					}, 100);
				}
			}
		};
		navigator.serviceWorker.addEventListener("message", handler);

		return () =>
			navigator.serviceWorker.removeEventListener("message", handler);
	}, [accounts, navigate]);

	return (
		<SettingsContext.Provider value={settings}>
			<Outlet
				context={{
					accounts,
					setAccounts,
					persistence,
					idbp,
					initialSelectedAccount,
					setSettings,
				}}
			/>
			<Call accounts={accounts} />
		</SettingsContext.Provider>
	);
};

const initialP = (async () => {
	const accountIds = await persistence.listAccounts();
	const initialSelectedAccount =
		(await persistence.get("ui#selectedAccount")) || accountIds[0];
	return {
		initialAccounts: Object.fromEntries(
			accountIds.map((id: string) => [id, newAccount(id, idbp, persistence)]),
		),
		initialSelectedAccount,
		initialSettings: {
			enterIsSend:
				"" + (await persistence.get("config#enterIsSend")) !== "false",
			richTextToolbar:
				"" + (await persistence.get("config#richTextToolbar")) === "true",
			audibleNotifications:
				"" + (await persistence.get("config#audibleNotifications")) === "true",
			messageRequests:
				(await persistence.get("config#messageRequests")) ?? "spam",
		},
	};
})();

export const App = () => {
	return (
		<Suspense fallback={<CircularProgress className="fullscreen" />}>
			<Await resolve={initialP}>
				<AppInner />
			</Await>
		</Suspense>
	);
};
